import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Collections, Results } from '@local/client-contracts';
import { resultsViewSettings } from '@local/common-web';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Filter, FilterChangeData } from '@shared/components/filters/models';
import { SyncsService } from '@shared/services/syncs.service';
import { resultHeaderCountKey } from '@shared/utils/header-builder.util';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { SearchOptions, SearchResultContext, SearchService, SearchSession } from 'src/app/bar/services/search';
import { SearchParamsService } from 'src/app/bar/services/search-params.service';
import { LinkResourcesResultExtra, LinkResourcesSourceSettings } from 'src/app/bar/services/search/client';
import { isLinkResourcesSettings } from 'src/app/bar/services/search/client/source-setings.util';
import { ScrollTrigger } from '../../../results';
import { ResultsFilterEvent, ResultsFilterEventOperation } from '../../models/result-filter-event.model';
import { CreateCollectionType } from '../../models/create-collection.type';
import { CollectionPopupService } from '../../services/collection-popup.service';
import { CollectionsComponentFocused } from '../collection-view/collection-view.component';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { KeyName, isKey, keyCodes } from '@local/ts-infra';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';
import { RouterService } from '@shared/services/router.service';
import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import { ValuesUpdatedEvent } from '@shared/components/filters/multi-select-filter-base.component';
import { collectionContent } from '../../helpers/collection.content';
import { LogService } from '@shared/services';
import { Logger } from '@unleash-tech/js-logger';

@UntilDestroy()
@Component({
  selector: 'live-collection',
  templateUrl: './live-collection.component.html',
  styleUrls: ['./live-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveCollectionComponent implements OnInit, OnDestroy {
  private logger: Logger;
  private _collection: Collections.LiveCollection;
  private searchSession: SearchSession;
  preFilters: Collections.SearchParams;
  loading: boolean;
  emptyResults$ = new Subject<boolean>();
  displayedContext$ = new BehaviorSubject<SearchResultContext>(null);
  items$ = new BehaviorSubject(null);
  currentState: any;
  searchId: string;
  CollectionsComponentFocused = CollectionsComponentFocused;
  canEdit: boolean;
  filters: Filter[];
  isFiltersChanged: boolean;
  private isNewCollection: boolean;
  private filtersSub: Subscription;

  //keyboard
  private keyHandlerId: string;

  @Input() set collection(val: Collections.LiveCollection) {
    this.updateFilters(val);
    this.isNewCollection = val.createdTime === val.modifiedTime;
    this._collection = val;
    this.canEdit = this.collectionsHelperService.canEdit(this.collectionsService.currentCollectionView);
    this.cdr.markForCheck();
  }

  @Input() state: CreateCollectionType;
  @Input() layoutMode: Results.LayoutType;
  @Input() updateHeight: EventEmitter<void>;

  _componentFocused: CollectionsComponentFocused;
  @Input() set componentFocused(focus: CollectionsComponentFocused) {
    this._componentFocused = focus;
    if (this._componentFocused === CollectionsComponentFocused.FILTERS) {
      this.hubService.focusPosition = 'filters';
    } else {
      this.hubService.focusPosition = 'results';
    }
  }

  get componentFocused(): CollectionsComponentFocused {
    return this._componentFocused;
  }

  @Output() componentFocusedFinish = new EventEmitter();
  @Output() updateLastSync = new EventEmitter();
  @Output() viewModeChanged = new EventEmitter();

  get collection() {
    return this._collection;
  }

  constructor(
    logger: LogService,
    private searchService: SearchService,
    public searchParamsService: SearchParamsService,
    private hubService: HubService,
    private filtersService: FiltersService,
    private cdr: ChangeDetectorRef,
    public collectionPopupService: CollectionPopupService,
    private resultsService: ResultsService,
    private syncsService: SyncsService,
    public collectionsService: CollectionsService,
    private showToasterService: ShowToasterService,
    private keyboardService: KeyboardService,
    private collectionsHelperService: CollectionsUtilService,
    private routerService: RouterService
  ) {
    this.logger = logger.scope('LiveCollectionComponent');
  }

  ngOnInit(): void {
    this.searchSession = this.searchService.getOrCreateSearchSession('collections');
    this.hubService.state$.pipe(untilDestroyed(this)).subscribe((state) => {
      const newState = cloneDeep(state);
      delete newState.fyi; // ignore fyi state change
      if (!isEqual(newState, this.currentState)) {
        this.currentState = newState;
        this.updateFilters(this.collection);
        this.onStateChanged();
      }
    });
    this.registerKeyboardHandler();
    this.routerService.queryParams$.pipe(untilDestroyed(this)).subscribe((params) => {
      const searchParams = this.collection?.searchParams;
      const queryChanged = (this.hubService.query || '') !== (searchParams?.query || '');
      this.isFiltersChanged = queryChanged || !isEqual(this.filtersService.inlineFilters, searchParams?.filters ?? {});
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
    this.searchSession?.destroy();
  }

  onSave() {
    this.isFiltersChanged = false;
    this.cdr.markForCheck();
  }

  onFilterChange(data: FilterChangeData) {
    const name = data.name;
    const action = data.action;
    if (name !== this.filtersService.FREE_TEXT_NAME) {
      this.resultsService.handleDataChanges(data);
    }
    this.onUpdateFilterBox({
      name,
      operation: action.toLowerCase() as ResultsFilterEventOperation,
      value: data.changes.values[0]?.value,
    });
  }

  clearAllFilters() {
    this.hubService.query = null;
    this.filtersService.removeAllFilters('all', false);
    this.onUpdateFilterBox({ name: null, operation: 'clearParams', value: null });
  }

  onStateChanged() {
    if (!this.hubService.query && isEmpty(this.filtersService.inlineFilters)) {
      this.searchParamsService.removeSort();
      this.emptyResults$.next(false);
      this.loading = false;
      this.updateLastSync.emit(null);
      if (this.displayedContext$.getValue()?.items?.length) {
        this.items$.next([]);
        this.displayedContext$.next(null);
      }
    } else {
      this.search();
    }
  }

  search() {
    this.loading = true;
    this.cdr.markForCheck();
    const trigger = 'navigation_tree';
    const options: SearchOptions = {
      resetSession: false,
      query: this.hubService.query,
      sources: [
        {
          id: 'link-resources',
          type: 'link-resources',
          requestMaxCount: undefined,
          sorting: this.searchParamsService.getSort(),
          caching: { strategy: 'cache-and-source' },
          lastSyncTimes: true,
          header: {
            title: `Showing ${resultHeaderCountKey} ${this.searchParamsService.innerQuery ? 'results' : 'items'}`,
          },
          noHeader: false,
          disableCloudGroup: true,
          useSourceFilters: true,
          filters: { preFilters: this.filtersService.inlineFilters, postFilters: {} },
          advancedSearch: true,
          contentSearch: true,
          tag: `live-collection-${trigger}`,
        } as LinkResourcesSourceSettings,
      ],
      trigger,
      telemetrySearchMethod: 'Quick-Search',
    };
    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx?.searchCompleted) {
          const resourceSource = ctx.sources?.find((s) => isLinkResourcesSettings(s.source));
          const extra = resourceSource?.extra as LinkResourcesResultExtra;
          this.searchId = extra?.searchId;
          this.updateLastSync.emit(extra?.lastSyncTime);
          this.displayedContext$.next(ctx);
          this.items$.next(ctx?.items);
          this.collectionsService.collectionResultData$.next({ count: extra?.cloudTotalResults, lastSyncTime: extra?.lastSyncTime });
          this.handleResultsLinksState();
          this.loading = false;
          this.emptyResults$.next(ctx && ctx.searchCompleted && ctx.items?.length == 0);
          this.cdr.markForCheck();
        }
      });
  }

  onFilterValuesSearchDone($event: ValuesUpdatedEvent) {
    const { name, values, filterInput } = $event;
    if (this.isFiltersChanged || filterInput) {
      return;
    }
    const searchParams = this.collection.searchParams;
    const activeFilters = searchParams?.filters?.[name];
    if (!activeFilters?.length) {
      return;
    }
    const filter = this.filters.find((f) => f.name === 'createdAt');
    const isTimeFilter = filter?.picker === 'time-dropdown';
    if (isTimeFilter) {
      return;
    }
    const newFilters: string[] = [];
    let missingFilters = false;
    const ids = new Set(values.map((v) => v.id));
    for (const value of activeFilters) {
      const id = `filter:${name}:value:${value}`;
      if (ids.has(id)) {
        newFilters.push(value);
      } else {
        this.logger.error('This filter is not supported in this collection', { name, value, id: this.collection.id });
        missingFilters = true;
      }
    }
    if (missingFilters) {
      this.showToasterService.showToaster({
        id: 'collection-missing-apps',
        title: collectionContent.missingFilters,
      });
      this.filtersService.setFilters(name, newFilters, 'pre');
    }
  }

  private handleResultsLinksState(): void {
    const { linksStateToasterData, onDestroy } = this.resultsService.handleResultsLinksState(
      this.displayedContext$.getValue().items,
      this.syncsService.linksSyncStatuses
    );
    if (linksStateToasterData) {
      this.showToasterService.showToaster(linksStateToasterData, onDestroy);
    }
  }

  nextPage(trigger: ScrollTrigger) {
    this.searchSession.nextPage$(trigger, [{ type: 'link-resources' }]);
  }

  onUpdateFilterBox(event: ResultsFilterEvent) {
    let searchParams = cloneDeep(this.collection?.searchParams);
    if (!searchParams) searchParams = { filters: {} };
    const collectionActions: Collections.UpdateAction[] = [];
    switch (event.operation) {
      case 'add':
        if (!searchParams?.filters) {
          searchParams.filters = {};
        }
        if (searchParams.filters[event.name]) {
          searchParams.filters[event.name].push(<string>event.value);
        } else {
          searchParams.filters[event.name] = [<string>event.value];
        }
        collectionActions.push({
          field: `searchParams.filters.${event.name}`,
          type: 'SetAdd',
          value: event.value,
        });
        break;
      case 'remove':
        if (!searchParams?.filters) {
          searchParams.filters = {};
        }
        if (searchParams.filters?.[event.name]?.length === 1 || !event.value) {
          delete searchParams.filters[event.name];
          this.collectionsService.removeDateInCollectionCustomDateRoaming(this.collection, event.name);
          collectionActions.push({
            field: `searchParams.filters.${event.name}`,
            type: 'Delete',
          });
        } else {
          searchParams.filters[event.name] = searchParams.filters[event.name]?.filter((elm) => elm !== <string>event.value);
          collectionActions.push({
            field: `searchParams.filters.${event.name}`,
            type: 'SetRemove',
            value: event.value,
          });
        }
        if (isEmpty(searchParams.filters)) {
          delete searchParams.filters;
        }
        break;
      case 'change':
        if ((event.value && event.value !== '') || (event.value && event.value !== '' && !isEqual(event.value, searchParams.sort))) {
          searchParams[event.name] = event.value;
          collectionActions.push({
            field: `searchParams.${event.name}`,
            type: 'Update',
            value: event.value,
          });
        }
        if (searchParams.query === '') {
          delete searchParams.query;
          collectionActions.push({
            field: 'searchParams.query',
            type: 'Delete',
          });
        }
        break;
      case 'set':
        if (event.name === this.filtersService.FREE_TEXT_NAME) {
          searchParams.query = event.value as string;
          collectionActions.push({
            field: 'searchParams.query',
            type: 'Update',
            value: event.value,
          });
        } else {
          if (!searchParams?.filters) {
            searchParams.filters = {};
          }
          searchParams.filters[event.name] = [<string>event.value];
          collectionActions.push({
            field: `searchParams.filters.${event.name}`,
            type: 'Update',
            value: event.value,
          });
        }
        break;
      case 'clearall':
        delete searchParams.filters[event.name];
        collectionActions.push({
          field: `searchParams.filters.${event.name}`,
          type: 'Delete',
        });
        break;
      case 'clearParams':
        searchParams = { ...searchParams, filters: {}, query: null, sort: null };
        collectionActions.push({
          field: 'searchParams.filters',
          type: 'Delete',
        });
        collectionActions.push({
          field: 'searchParams.sort',
          type: 'Delete',
        });
        collectionActions.push({
          field: 'searchParams.query',
          type: 'Delete',
        });
        break;
      default:
        break;
    }

    if (this.state === 'edit') {
      if (isEqual(searchParams, this.collection.searchParams)) {
        searchParams = null;
      }
    }
  }

  onViewModeChanged() {
    this.searchParamsService.layoutMode = this.layoutMode === 'list' ? 'gallery' : 'list';
    if (!this.isNewCollection) {
      this.viewModeChanged.emit();
    }
  }

  private updateFilters(collection: Collections.LiveCollection) {
    if (!collection) {
      this.filters = [];
      return;
    }
    const searchParams = collection?.searchParams;
    const searchParamsFilters = searchParams?.filters || {};
    const { filters, moreFilters, spreadAsMultiSelect, includeSelected } = resultsViewSettings['live-collection'];
    const base = filters.map((v) => v.name);
    const nonBaseActiveFilters = Object.keys(searchParamsFilters).filter((v) => !base.includes(v) && this.filtersService.allFilters[v]);
    const baseFilterNames = [...base, ...nonBaseActiveFilters];

    this.filtersSub?.unsubscribe();
    this.filtersSub = this.filtersService
      .getFloatingResultsFilters({
        sessionName: `live-collection-${collection.id}`,
        typeSuggestedFilters: [],
        moreFilters,
        baseFilters: baseFilterNames.map((n) => ({ name: n })),
        spreadAsMultiSelect,
        allowEmptyFilter: true,
        includeSelected,
        optionsSuggestionProvider: {
          retrieveSelected: true,
        },
      })
      .subscribe((f) => {
        this.filters = f;
        this.cdr.markForCheck();
      });
  }

  //@@@ KEY HANDLING

  registerKeyboardHandler() {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.handleKeys(keys, event);
    }, 9);
    this.cdr.markForCheck();
  }

  private unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
  }

  async handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    if (this.componentFocused === CollectionsComponentFocused.FILTERS) {
      this.hubService.focusPosition = 'filters';
      if (isKey(event, keyCodes.ArrowDown)) {
        this.componentFocusedFinish.emit('next');
      }
      if (isKey(event, keyCodes.ArrowUp)) {
        this.componentFocusedFinish.emit('prev');
      }
    }
  }
}
